import { useState, useEffect, useRef, useCallback, type PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { Plus, MessageSquare, Menu, X, Loader2, Inbox } from "lucide-react";
import type { ApiClient } from "@/lib/api/chatApi";
import type { ActivityItem, ApprovalItem } from "@/lib/api/approvalsApi";
import ApprovalList from "@/components/approvals/ApprovalList";
import { agentIcon } from "@/lib/agentColors";
import { iniciarSondeo } from "@/lib/polling";
import { fusionarPrimeraPagina, tituloPestana } from "@/lib/threads";

const PAGE_SIZE = 20;

/**
 * Ancho del sidebar (arrastrable en md+, persistido en localStorage). Mismo patron
 * que useDocPanelResize en ApprovalDetail: el usuario elige el ancho y el navegador
 * lo RECUERDA entre sesiones. En movil el sidebar es un overlay a ancho fijo, asi
 * que el divisor se oculta y el ancho no se aplica (ver `md:` en el markup).
 *
 * El MINIMO (364px) es el ancho medido en el navegador con el que la bandeja de
 * aprobaciones se ve entera: las tres pestanas (Pendientes/Resueltas/Registro) con
 * su etiqueta legible Y el boton de refresco a su derecha sin solaparse. Por debajo
 * de ese ancho las etiquetas empiezan a truncarse. El truncado ya no puede COMER el
 * boton (el tablist lleva `min-w-0` en ApprovalList), pero el minimo evita llegar
 * ahi: es el suelo de "se ve correcto", no el suelo de "no se rompe".
 */
const SIDEBAR_W_KEY = "sidebarWidthPx";
const SIDEBAR_W_MIN = 364;
const SIDEBAR_W_MAX = 560;
const SIDEBAR_W_DEFAULT = 364;

function useSidebarResize() {
  const [width, setWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem(SIDEBAR_W_KEY));
    return Number.isFinite(saved) && saved >= SIDEBAR_W_MIN && saved <= SIDEBAR_W_MAX
      ? saved
      : SIDEBAR_W_DEFAULT;
  });
  const asideRef = useRef<HTMLElement>(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current || !asideRef.current) return;
      const left = asideRef.current.getBoundingClientRect().left;
      setWidth(Math.min(SIDEBAR_W_MAX, Math.max(SIDEBAR_W_MIN, e.clientX - left)));
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      setWidth((px) => {
        try {
          localStorage.setItem(SIDEBAR_W_KEY, String(Math.round(px)));
        } catch { /* localStorage no disponible: no persiste, sin romper */ }
        return px;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const onDividerPointerDown = (e: ReactPointerEvent) => {
    draggingRef.current = true;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    e.preventDefault();
  };

  return { width, asideRef, onDividerPointerDown };
}

interface Thread {
  thread_id: string;
  last_checkpoint: string;
  assistant_id?: string;
  created_at?: string;
  title?: string;
}

export type SidebarView = "chats" | "approvals";

interface SidebarProps {
  api: ApiClient;
  onNewChat: () => void;
  activeThreadId: string | null;
  onSelectThread: (threadId: string, assistantId: string) => void;
  // Vista activa (chats | aprobaciones) y su conmutador.
  view: SidebarView;
  onChangeView: (view: SidebarView) => void;
  // Bandeja de aprobaciones (la gestiona el padre via useApprovalsInbox).
  approvalItems: ApprovalItem[];
  approvalsLoading: boolean;
  // Agentes HITL accesibles: grupos fijos del arbol aunque no tengan pendientes.
  hitlAgents?: string[];
  // El usuario tiene permiso a un agente HITL -> mostrar SIEMPRE la pestana
  // Aprobaciones (aunque haya 0 pendientes).
  canApprove: boolean;
  selectedApprovalId: string | null;
  onSelectApproval: (item: ApprovalItem) => void;
  // Resueltas por agente (cargadas bajo demanda al expandir el subgrupo).
  resolvedByAgent?: Record<string, ApprovalItem[]>;
  resolvedLoading?: Record<string, boolean>;
  resolvedHasMore?: Record<string, boolean>;
  onExpandResolved?: (agentId: string) => void;
  onLoadMoreResolved?: (agentId: string) => void;
  onLoadMoreActivity?: (agentId: string) => void;
  pendingHasMore?: boolean;
  // Registro de actividad por agente (cargado bajo demanda al abrir la pestana).
  activityByAgent?: Record<string, ActivityItem[]>;
  activityLoading?: Record<string, boolean>;
  activityHasMore?: Record<string, boolean>;
  onExpandActivity?: (agentId: string) => void;
}

export default function Sidebar({
  api,
  onNewChat,
  activeThreadId,
  onSelectThread,
  view,
  onChangeView,
  approvalItems,
  approvalsLoading,
  hitlAgents,
  canApprove,
  selectedApprovalId,
  onSelectApproval,
  resolvedByAgent,
  resolvedLoading,
  resolvedHasMore,
  onExpandResolved,
  onLoadMoreResolved,
  onLoadMoreActivity,
  pendingHasMore,
  activityByAgent,
  activityLoading,
  activityHasMore,
  onExpandActivity,
}: SidebarProps) {
  const { t } = useTranslation();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [open, setOpen] = useState(false);
  const { width, asideRef, onDividerPointerDown } = useSidebarResize();
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [backendVersion, setBackendVersion] = useState("");
  const sentinelRef = useRef<HTMLDivElement>(null);
  const endReachedRef = useRef(false);
  const pagesLoadedRef = useRef(0);

  useEffect(() => {
    api.getInfo().then((info) => setBackendVersion(info.version)).catch(() => {});
  }, [api]);

  // Titulo de la pestaña = resumen de la conversacion activa (el que pinta esta
  // lista, refrescado por el sondeo: el resumen se genera ~1 s despues del turno).
  const tituloActivo = threads.find((t) => t.thread_id === activeThreadId)?.title ?? null;
  useEffect(() => {
    document.title = tituloPestana(tituloActivo);
  }, [tituloActivo]);
  useEffect(() => () => { document.title = tituloPestana(null); }, []);

  // Load first page + poll for new threads.
  // El sondeo NUNCA se apaga por fallos: antes, 3 errores seguidos hacian
  // clearInterval y la sidebar dejaba de refrescarse hasta recargar la pagina.
  // Un reinicio del backend (deploy, rebuild local) tarda mas de 15 s, asi que
  // lo disparaba siempre y las conversaciones nuevas "no aparecian". Ahora
  // `iniciarSondeo` (lib/polling.ts) reprograma con retroceso exponencial
  // (5→10→20→40, tope 60 s) mientras falla, vuelve a 5 s al recuperarse, no
  // sondea con la pestaña oculta y avisa en consola solo en el flanco ok→fallo.
  useEffect(() => {
    const fetchFirstPage = () =>
      api.listThreads(PAGE_SIZE, 0).then((data) => {
        // Sustituye la primera pagina y conserva las ya cargadas, sin duplicar
        // filas (ver `fusionarPrimeraPagina` en lib/threads.ts).
        setThreads((prev) => fusionarPrimeraPagina(prev, data, PAGE_SIZE));
        if (!endReachedRef.current) setHasMore(data.length >= PAGE_SIZE);
      });

    return iniciarSondeo(fetchFirstPage, {
      onFallo: (err) => console.error("Sidebar: no se pudo refrescar la lista de conversaciones", err),
      onRecuperado: () => console.info("Sidebar: la lista de conversaciones vuelve a refrescarse"),
    });
  }, [api]);

  // Load more pages on scroll
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const offset = (pagesLoadedRef.current + 1) * PAGE_SIZE;
    api
      .listThreads(PAGE_SIZE, offset)
      .then((data) => {
        if (data.length < PAGE_SIZE) {
          setHasMore(false);
          endReachedRef.current = true;
        }
        pagesLoadedRef.current++;
        if (data.length > 0) {
          setThreads((prev) => {
            const existingIds = new Set(prev.map((t) => t.thread_id));
            const newThreads = data.filter((t) => !existingIds.has(t.thread_id));
            return [...prev, ...newThreads];
          });
        }
      })
      .catch(console.error)
      .finally(() => setLoadingMore(false));
  }, [api, loadingMore, hasMore]);

  // IntersectionObserver on sentinel element
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <>
      {/* Mobile hamburger — below header (top-[3.75rem] = h-14 + margin) */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed left-2 top-[3.75rem] z-40 rounded p-2 md:hidden hover:bg-[var(--muted)]"
      >
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar. En md+ el ancho lo manda `--sidebar-w` (arrastrable y persistido
          en localStorage); en movil es un overlay a ancho fijo w-70, donde arrastrar
          no tiene sentido y el divisor se oculta. */}
      <aside
        ref={asideRef}
        style={{ ["--sidebar-w" as string]: `${width}px` }}
        className={`
          fixed top-14 bottom-0 left-0 z-40 w-70 border-r border-[var(--border)]
          bg-[var(--sidebar-bg)] transition-transform
          md:static md:w-[var(--sidebar-w)] md:shrink-0 md:translate-x-0
          ${open ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <div className="flex h-full flex-col">
          {/* Conmutador Chats | Aprobaciones. La pestana de aprobaciones esta SIEMPRE
              presente si el usuario tiene permiso a un agente HITL (canApprove),
              aunque la bandeja este a 0; el contador solo aparece si hay pendientes. */}
          {canApprove && (
            <div className="flex shrink-0 gap-1 p-2">
              <button
                onClick={() => onChangeView("chats")}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  view === "chats"
                    ? "bg-[var(--muted)] text-[var(--foreground)]"
                    : "text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                }`}
              >
                <MessageSquare size={14} />
                {t("sidebar.chatsTab")}
              </button>
              <button
                onClick={() => onChangeView("approvals")}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  view === "approvals"
                    ? "bg-[var(--muted)] text-[var(--foreground)]"
                    : "text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                }`}
              >
                <Inbox size={14} />
                {t("approvals.menu")}
                {approvalItems.length > 0 && (
                  <span className="rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white">
                    {approvalItems.length}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Vista APROBACIONES: lista de pendientes. `pr-2` reserva el hueco de la
              barra de scroll: sin el, la barra se dibuja ENCIMA del boton de refresco
              de Resueltas/Registro, que vive en el borde derecho de la lista. */}
          {view === "approvals" ? (
            <div className="flex-1 overflow-y-auto pr-2">
              <ApprovalList
                items={approvalItems}
                agentIds={hitlAgents}
                selectedId={selectedApprovalId}
                onSelect={(item) => {
                  onSelectApproval(item);
                  setOpen(false);
                }}
                loading={approvalsLoading}
                resolvedByAgent={resolvedByAgent}
                resolvedLoading={resolvedLoading}
                resolvedHasMore={resolvedHasMore}
                onExpandResolved={onExpandResolved}
                onLoadMoreResolved={onLoadMoreResolved}
                pendingHasMore={pendingHasMore}
                activityByAgent={activityByAgent}
                activityLoading={activityLoading}
                activityHasMore={activityHasMore}
                onLoadMoreActivity={onLoadMoreActivity}
                onExpandActivity={onExpandActivity}
              />
            </div>
          ) : (
          <>
          {/* Fixed header: new chat + history title */}
          <div className="shrink-0 p-4 pb-2">
            <button
              onClick={onNewChat}
              className="flex items-center justify-center gap-2 rounded-full bg-[var(--primary)] px-3 py-2 text-sm font-medium text-white w-full"
            >
              <Plus size={16} />
              {t("chat.newChat")}
            </button>
          </div>

          {/* Scrollable thread list */}
          <div className="relative flex-1 overflow-y-auto px-4 pt-3 pb-4 [mask-image:linear-gradient(to_bottom,transparent,black_12px)]">
              <ul className="flex flex-col gap-1">
                {threads.length === 0 && (
                  <li className="px-3 py-2 text-sm text-[var(--muted-foreground)]">
                    {t("sidebar.noHistory")}
                  </li>
                )}
                {threads.map((thread) => {
                  // Icono e identidad de color del agente del hilo (gris + Bot si
                  // el agente no esta declarado en lib/agentColors).
                  const { icon: ThreadAgentIcon, color: threadAgentColor } =
                    agentIcon(thread.assistant_id ?? "");
                  return (
                  <li key={thread.thread_id}>
                    <button
                      onClick={() => thread.assistant_id && onSelectThread(thread.thread_id, thread.assistant_id)}
                      className={`flex w-full items-start gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-all cursor-pointer
                        ${activeThreadId === thread.thread_id
                          ? "bg-[var(--muted)] font-medium text-[var(--foreground)] shadow-sm border border-[var(--border)]"
                          : "text-[var(--foreground)] opacity-80 hover:bg-[var(--muted)] hover:opacity-100 hover:shadow-sm"
                        }`}
                    >
                      {/* El icono del agente SUSTITUYE al MessageSquare generico: en
                          una lista donde todas las filas son conversaciones, el
                          marcador comun no informaba, y el icono a color identifica
                          el agente de un vistazo (igual que en la cabecera del chat
                          y el selector de agente). */}
                      <ThreadAgentIcon size={14} className={`mt-0.5 shrink-0 ${threadAgentColor}`} />
                      <div className="min-w-0 flex-1">
                        {thread.assistant_id && (
                          <span className="mb-1 inline-flex items-center text-[11px] font-medium text-[var(--muted-foreground)]">
                            {t(`agents.${thread.assistant_id}.label`, thread.assistant_id)}
                          </span>
                        )}
                        <span className="block text-sm leading-snug">
                          {thread.title
                            || thread.thread_id.slice(0, 8)}
                        </span>
                        {thread.created_at && (
                          <span className="block text-xs text-[var(--muted-foreground)] opacity-70">
                            {new Date(thread.created_at).toLocaleString(undefined, {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                  );
                })}
              </ul>

              {/* Sentinel for infinite scroll */}
              {hasMore && (
                <div ref={sentinelRef} className="flex justify-center py-3">
                  {loadingMore && <Loader2 size={16} className="animate-spin text-[var(--muted-foreground)]" />}
                </div>
              )}
          </div>
          </>
          )}

          {/* Version footer */}
          {backendVersion && (
            <div className="border-t border-[var(--border)] px-4 py-1.5 text-[11px] text-[var(--muted-foreground)] opacity-50">
              {t("sidebar.versions")}: b{backendVersion} · f{__APP_VERSION__}
            </div>
          )}
        </div>
      </aside>

      {/* Divisor arrastrable del sidebar (solo md+, donde el sidebar es una columna
          del layout). Mismo gesto que el divisor del panel del documento. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t("sidebar.resize")}
        onPointerDown={onDividerPointerDown}
        className="hidden shrink-0 cursor-col-resize items-stretch px-1 md:flex"
      >
        <div className="w-px flex-1 rounded bg-[var(--border)] transition-colors hover:bg-[var(--ring)]" />
      </div>

      {/* Mobile overlay */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
        />
      )}
    </>
  );
}
